import type {
    ActorEvent,
    RecordLikeProtocol,
    RichTextContentProtocol,
    SelectedMode,
    TableColumns,
    TableColumnWidth,
    TableViewConfig,
    ViewField
} from '@lighthouse/core'
import { VIEW_DESIGN_STYLE } from '@lighthouse/core'
import { getViewColumns, ViewEmptyState } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { memo, useCallback, useMemo, useRef } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import * as SC from './styles'
import { Table } from './Table'
import { getDesignStyleMap } from './utils'

interface TableBlockProps {
    tablePropsCache?: TableColumns
    selectedRecords: string[]
    blockWidth?: number
    tableColumnCache?: TableColumnWidth
    onSelectModeChange?: (mode?: SelectedMode) => void
    onSelectedRecords: (recordIds: string[]) => void
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record: RecordLikeProtocol) => Promise<void>
    recordClickable?: boolean
    onRenderRichText: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
    onTableColumnWidthChange?: (val: TableColumnWidth) => void
}

/**
 *
 * @param param
 * @returns
 * blockData: {
 *   fields: { id: string; type: string; name: string }[]
 *   records: { id: string; content: Record<number, string> }[]
 * }
 */

const TableBlock: React.FC<TableBlockProps> = ({
    tablePropsCache,
    selectedRecords,
    blockWidth,
    tableColumnCache,
    onSelectModeChange,
    onSelectedRecords,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderRichText,
    onTableColumnWidthChange,
    recordClickable
}) => {
    const tableBlockRef = useRef<HTMLDivElement>(null)
    const { dataSource, recordPoints, blockData } = useViewBlockContext()
    const { schema, appId: dsAppId, viewOptions } = dataSource
    const { tableProps } = viewOptions
    const { id: blockId, config } = blockData
    const {
        canDelete,
        canExport,
        canPrint,
        canDisplay,
        viewFieldSettings,
        designStyle = VIEW_DESIGN_STYLE.classic,
        listAvatar,
        isShowListAvatar,
        avatarType,
        hiddenHeader,
        highLightRules,
        tableRowHeight,
        toolbar,
        recordOperator,
        breakPoint: baseBreakPointConfig,
        anchor
    } = config as TableViewConfig
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { emptyView } = baseBreakPointConfig || {}
    const noRecords = recordPoints && recordPoints.length === 0
    const tableRecordCheckable = !!canDelete || !!canExport || !!canPrint

    const designConfig = useMemo(
        () => ({
            designStyle,
            listAvatar,
            isShowListAvatar,
            avatarType,
            hiddenHeader
        }),
        [avatarType, designStyle, hiddenHeader, isShowListAvatar, listAvatar]
    )

    const designStyleOption = useMemo(() => getDesignStyleMap(designStyle), [designStyle])
    const { isContentBorder, isContentMargin } = designStyleOption

    // block 设置后的tableProps
    const settingColumns = useMemo(
        () =>
            getViewColumns({
                blockId,
                tableProps,
                value: viewFieldSettings,
                schema,
                tableColumn: tableColumnCache
            }),
        [blockId, schema, tableColumnCache, tableProps, viewFieldSettings]
    )

    // 用户字段设置后的tableProps
    const [columns, userColumns] = useMemo(() => {
        if (!tablePropsCache || tablePropsCache.length === 0 || !canDisplay) {
            const list = settingColumns.filter(item => {
                return item.visible
            })
            return [list, list]
        }

        const columnsCache = tablePropsCache.reduce<ViewField[]>((prev, cur) => {
            const field = find(({ fieldId }) => fieldId === cur.id, settingColumns)
            if (field) {
                prev.push({
                    ...field,
                    visible: cur.visible
                })
            }
            return prev
        }, [])
        const columns = settingColumns.reduce<ViewField[]>((prev, cur) => {
            if (!cur.visible) {
                return prev
            }
            const field = find(({ fieldId }) => fieldId === cur.fieldId, prev)
            if (!field) {
                prev.push({
                    ...cur,
                    visible: false
                })
            }
            return prev
        }, columnsCache)

        const userColumns = columns.filter(item => item.visible)
        return [columns, userColumns]
    }, [tablePropsCache, canDisplay, settingColumns])

    const handleSelect = useCallback(
        (recordIds: string[]) => {
            onSelectedRecords(recordIds)
        },
        [onSelectedRecords]
    )

    return (
        <SC.TableScrollerContent
            id={effectiveAnchorId}
            bordered={isContentBorder}
            margin={!isContentMargin}
            data-content-type="Table"
            styles={{
                scrollbar: {
                    zIndex: 2
                }
            }}
            viewportRef={tableBlockRef}
        >
            <Table
                checkable={tableRecordCheckable}
                highLightRules={highLightRules}
                scrollContainerRef={tableBlockRef}
                viewFields={userColumns}
                blockWidth={blockWidth}
                // designStyle={designStyle}
                designConfig={designConfig}
                selectedRecords={selectedRecords}
                tableRowHeight={tableRowHeight}
                onSelectModeChange={onSelectModeChange}
                onRecordSelect={handleSelect}
                onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                recordOperatorActions={recordOperator}
                onRecordClick={onRecordClick}
                onRenderButtonTitle={onRenderRichText}
                onTableColumnWidthChange={onTableColumnWidthChange}
                recordClickable={recordClickable}
            />
            {noRecords && (
                <SC.TableNoDataContent width={blockWidth}>
                    <ViewEmptyState config={emptyView} onRenderRichText={onRenderRichText} />
                </SC.TableNoDataContent>
            )}
        </SC.TableScrollerContent>
    )
}

export default memo(TableBlock)
